import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import {
  DISCUSS_AUTO_RULE_MODE_PRICE,
  DISCUSS_AUTO_RULE_MODE_RATIO,
  makeDiscussAutoRuleId,
  normalizeDiscussAutoRule,
  readDiscussAutoRulesFromPreferences,
} from '@/temu-platform/modules/discuss/discussAutoRules';
import { showToast } from '@/utils/toast';

function formatNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '—';
}

/** 三档文案：低于拒绝 / 中间人工 / 不低于同意 */
function discussAutoRuleBands(ruleOrReject, acceptMaybe, modeMaybe) {
  const mode = typeof ruleOrReject === 'object' && ruleOrReject
    ? (ruleOrReject.mode === DISCUSS_AUTO_RULE_MODE_PRICE
      ? DISCUSS_AUTO_RULE_MODE_PRICE
      : DISCUSS_AUTO_RULE_MODE_RATIO)
    : (modeMaybe === DISCUSS_AUTO_RULE_MODE_PRICE
      ? DISCUSS_AUTO_RULE_MODE_PRICE
      : DISCUSS_AUTO_RULE_MODE_RATIO);
  if (mode === DISCUSS_AUTO_RULE_MODE_PRICE) {
    const lo = formatNum(typeof ruleOrReject === 'object' ? ruleOrReject.rejectBelowPrice : ruleOrReject);
    const hi = formatNum(typeof ruleOrReject === 'object' ? ruleOrReject.acceptAbovePrice : acceptMaybe);
    return {
      reject: `<${lo}元 拒绝`,
      manual: `${lo}～${hi}元 人工`,
      accept: `≥${hi}元 同意`,
    };
  }
  const lo = formatNum(typeof ruleOrReject === 'object' ? ruleOrReject.rejectBelowPercent : ruleOrReject);
  const hi = formatNum(typeof ruleOrReject === 'object' ? ruleOrReject.acceptAbovePercent : acceptMaybe);
  return {
    reject: `<${lo}% 拒绝`,
    manual: `${lo}%～${hi}% 人工`,
    accept: `≥${hi}% 同意`,
  };
}

/**
 * 核价自动规则：按类目配置比例或绝对价格阈值，同步后自动同意/拒绝。
 */
export default function DiscussAutoRuleModal({
  open,
  shopId,
  busy = false,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftCategoryLabel, setDraftCategoryLabel] = useState('');
  const [draftProductTypeId, setDraftProductTypeId] = useState('');
  const [draftMode, setDraftMode] = useState(DISCUSS_AUTO_RULE_MODE_RATIO);
  const [draftReject, setDraftReject] = useState('50');
  const [draftAccept, setDraftAccept] = useState('90');

  const leafOptions = useMemo(() => flattenLeafCategories(categories), [categories]);

  const resetDraft = useCallback(() => {
    setDraftCategoryId('');
    setDraftCategoryLabel('');
    setDraftProductTypeId('');
    setDraftMode(DISCUSS_AUTO_RULE_MODE_RATIO);
    setDraftReject('50');
    setDraftAccept('90');
  }, []);

  useEffect(() => {
    if (!open) return;
    resetDraft();
  }, [open, shopId, resetDraft]);

  useEffect(() => {
    if (!open || !shopId) {
      setCategories([]);
      setRules([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setLoading(true);
    Promise.all([
      temuPlatformAPI.getShopCategories(shopId).catch(() => null),
      temuPlatformAPI.getPreferences().catch(() => null),
    ])
      .then(([catRes, prefRes]) => {
        if (cancelled) return;
        setCategories(catRes?.categories ?? catRes?.items ?? catRes?.list ?? catRes ?? []);
        const prefs = prefRes?.preferences || prefRes || {};
        setRules(readDiscussAutoRulesFromPreferences(prefs, shopId));
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [open, shopId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, busy, onClose]);

  const onCategoryChange = (value) => {
    const id = String(value || '').trim();
    setDraftCategoryId(id);
    if (id === '*') {
      setDraftCategoryLabel('全部类目');
      setDraftProductTypeId('');
      return;
    }
    const opt = leafOptions.find((o) => String(o.categoryId) === id);
    setDraftCategoryLabel(opt?.label || '');
    setDraftProductTypeId(opt?.productTypeId || '');
  };

  const onModeChange = (nextMode) => {
    setDraftMode(nextMode);
    if (nextMode === DISCUSS_AUTO_RULE_MODE_PRICE) {
      setDraftReject('1');
      setDraftAccept('5');
    } else {
      setDraftReject('50');
      setDraftAccept('90');
    }
  };

  const addRule = () => {
    if (!draftCategoryId) {
      showToast(fixedUi("请选择类目"), 'warning');
      return;
    }
    const lo = Number(draftReject);
    const hi = Number(draftAccept);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      showToast(fixedUi("请填写有效数字"), 'error');
      return;
    }
    if (draftMode === DISCUSS_AUTO_RULE_MODE_PRICE) {
      if (lo < 0 || hi < 0) {
        showToast(fixedUi("价格阈值不能为负数"), 'error');
        return;
      }
      if (lo >= hi) {
        showToast(fixedUi("拒绝价格须小于同意价格，中间留给人工处理"), 'error');
        return;
      }
    } else {
      if (lo < 0 || lo > 100 || hi < 0 || hi > 100) {
        showToast(fixedUi("比例阈值请填写 0～100 的数字"), 'error');
        return;
      }
      if (lo >= hi) {
        showToast(fixedUi("拒绝阈值须小于同意阈值，中间留给人工处理"), 'error');
        return;
      }
    }
    if (rules.some((r) => String(r.categoryId) === String(draftCategoryId))) {
      showToast(fixedUi("该类目已有规则，请先删除后再添加"), 'warning');
      return;
    }
    const payload = {
      id: makeDiscussAutoRuleId(),
      enabled: true,
      mode: draftMode,
      categoryId: draftCategoryId,
      categoryPathLabel: draftCategoryLabel || (draftCategoryId === '*' ? '全部类目' : draftCategoryId),
      productTypeId: draftProductTypeId,
    };
    if (draftMode === DISCUSS_AUTO_RULE_MODE_PRICE) {
      payload.rejectBelowPrice = lo;
      payload.acceptAbovePrice = hi;
    } else {
      payload.rejectBelowPercent = lo;
      payload.acceptAbovePercent = hi;
    }
    const next = normalizeDiscussAutoRule(payload);
    if (!next) {
      showToast(fixedUi("规则无效"), 'error');
      return;
    }
    setRules((prev) => [...prev, next]);
    resetDraft();
    showToast(fixedUi("已加入规则列表，请点击保存生效"), 'info');
  };

  const removeRule = (id) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const toggleRule = (id, enabled) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled } : r)));
  };

  const handleSave = async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    setSaving(true);
    try {
      const prefRes = await temuPlatformAPI.getPreferences();
      const prefs = prefRes?.preferences || prefRes || {};
      const existingByShop = prefs?.discussAutoRulesByShop && typeof prefs.discussAutoRulesByShop === 'object'
        ? { ...prefs.discussAutoRulesByShop }
        : {};
      existingByShop[String(shopId)] = rules.map(normalizeDiscussAutoRule).filter(Boolean);
      await temuPlatformAPI.updatePreferences({
        activeShopId: Number(shopId) || undefined,
        preferences: {
          discussAutoRulesByShop: existingByShop,
        },
      });
      showToast(fixedUi("自动规则已保存"), 'success');
      onSaved?.(rules);
      onClose?.();
    } catch (e) {
      showToast(e?.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const disabled = busy || saving || loading;
  const draftBands = discussAutoRuleBands(draftReject, draftAccept, draftMode);
  const isPriceMode = draftMode === DISCUSS_AUTO_RULE_MODE_PRICE;

  return createPortal(
    <div className="tpo-batch-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={disabled}
        onClick={() => !disabled && onClose?.()}
      />
      <div
        className="tpo-batch-modal__panel tpo-discuss-auto-rule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-discuss-auto-rule-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-discuss-auto-rule-title">{fixedUi("核价自动规则")}</h2>
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
            disabled={disabled}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>

        <div className="tpo-batch-modal__body">
          <p className="tpo-batch-modal__hint">
            {isPriceMode
              ? '按平台建议价（元）判断：低于拒绝价自动拒绝，达到同意价自动同意；中间需人工处理。同步核价单后自动执行启用中的规则。'
              : '按「建议价 ÷ 申报价」比例判断。例：申报 20、建议 10 → 50%。同步核价单后自动执行启用中的规则。'}
          </p>
          <div className="tpo-discuss-auto-rule-bands">
            <span className="tpo-discuss-auto-rule-bands__seg tpo-discuss-auto-rule-bands__seg--reject">{draftBands.reject}</span>
            <span className="tpo-discuss-auto-rule-bands__seg tpo-discuss-auto-rule-bands__seg--manual">{draftBands.manual}</span>
            <span className="tpo-discuss-auto-rule-bands__seg tpo-discuss-auto-rule-bands__seg--accept">{draftBands.accept}</span>
          </div>

          <div className="tpo-discuss-auto-rule-form">
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{fixedUi("适用类目")}</span>
              <CategoryCascaderSelect
                shopId={shopId}
                categories={categories}
                value={draftCategoryId}
                label={draftCategoryLabel}
                disabled={disabled || metaLoading || !shopId}
                placeholder={metaLoading ? '类目加载中…' : fixedUi("请选择或搜索类目")}
                emptyText={!shopId ? fixedUi("请先选择店铺") : fixedUi("暂无可发商品分类")}
                includeAllOption
                onChange={onCategoryChange}
              />
            </label>
            <div className="tpo-discuss-auto-rule-form__row tpo-discuss-auto-rule-form__row--mode">
              <span className="tpo-field__label">{fixedUi("判断方式")}</span>
              <div className="tpo-discuss-auto-rule-mode" role="group" aria-label={fixedUi("判断方式")}>
                <button
                  type="button"
                  className={`tpo-btn tpo-btn--sm${draftMode === DISCUSS_AUTO_RULE_MODE_RATIO ? ' tpo-btn--primary' : ' tpo-btn--secondary'}`}
                  disabled={disabled}
                  onClick={() => onModeChange(DISCUSS_AUTO_RULE_MODE_RATIO)}
                >
                  按比例
                </button>
                <button
                  type="button"
                  className={`tpo-btn tpo-btn--sm${draftMode === DISCUSS_AUTO_RULE_MODE_PRICE ? ' tpo-btn--primary' : ' tpo-btn--secondary'}`}
                  disabled={disabled}
                  onClick={() => onModeChange(DISCUSS_AUTO_RULE_MODE_PRICE)}
                >
                  按价格
                </button>
              </div>
            </div>
            <div className="tpo-discuss-auto-rule-form__row">
              <label className="tpo-field">
                <span className="tpo-field__label">{isPriceMode ? fixedUi("低于（元）拒绝") : fixedUi("低于 % 拒绝")}</span>
                <input
                  className="tpo-input"
                  type="number"
                  min={0}
                  max={isPriceMode ? undefined : 100}
                  step={isPriceMode ? 0.01 : 1}
                  value={draftReject}
                  disabled={disabled}
                  onChange={(e) => setDraftReject(e.target.value)}
                />
              </label>
              <label className="tpo-field">
                <span className="tpo-field__label">{isPriceMode ? fixedUi("不低于（元）同意") : fixedUi("不低于 % 同意")}</span>
                <input
                  className="tpo-input"
                  type="number"
                  min={0}
                  max={isPriceMode ? undefined : 100}
                  step={isPriceMode ? 0.01 : 1}
                  value={draftAccept}
                  disabled={disabled}
                  onChange={(e) => setDraftAccept(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="tpo-btn tpo-btn--secondary tpo-btn--sm tpo-discuss-auto-rule-form__add"
                disabled={disabled}
                onClick={addRule}
              >
                添加
              </button>
            </div>
          </div>

          <div className="tpo-discuss-auto-rule-list">
            <div className="tpo-discuss-auto-rule-list__head">{fixedUi('已配置规则')}（{rules.length}）</div>
            {loading ? (
              <p className="tpo-batch-modal__hint">{fixedUi("加载中…")}</p>
            ) : rules.length === 0 ? (
              <p className="tpo-batch-modal__hint">{fixedUi("暂无规则，选类目并填写阈值后添加。")}</p>
            ) : (
              <ul className="tpo-discuss-auto-rule-list__ul">
                {rules.map((rule) => {
                  const bands = discussAutoRuleBands(rule);
                  const catLabel = rule.categoryPathLabel || rule.categoryId;
                  const modeLabel = rule.mode === DISCUSS_AUTO_RULE_MODE_PRICE ? '价格' : '比例';
                  return (
                    <li key={rule.id} className="tpo-discuss-auto-rule-list__item">
                      <div className="tpo-discuss-auto-rule-list__main">
                        <span className="tpo-discuss-auto-rule-list__cat" title={catLabel}>
                          [{modeLabel}] {catLabel}
                        </span>
                        <span>{bands.reject} · {bands.manual} · {bands.accept}</span>
                      </div>
                      <div className="tpo-discuss-auto-rule-list__ops">
                        <TemuSwitch
                          checked={rule.enabled !== false}
                          disabled={disabled}
                          ariaLabel={`${catLabel || '规则'}启用`}
                          onChange={(next) => toggleRule(rule.id, next)}
                        />
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                          disabled={disabled}
                          onClick={() => removeRule(rule.id)}
                        >
                          {fixedUi("删除")}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="tpo-batch-modal__foot">
          <button
            type="button"
            className="tpo-btn tpo-btn--secondary"
            disabled={disabled}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary"
            disabled={disabled || !shopId}
            onClick={handleSave}
          >
            {saving ? fixedUi("保存中…") : fixedUi("保存")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
